import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { portalTheme as t, ensurePortalFonts, PORTAL_HEADLINE_STATS } from './theme';
import { setPortalToken, clearPortalImpersonator } from './auth';

/* Client portal sign-in. Designed as a self-contained page — no imports
 * from the intranet's routes/, app/, or features/ directories — so the
 * whole `portal/` tree can be lifted into a standalone application later
 * with only a build-config change. Uses raw fetch instead of the Redux/
 * RTK Query stack for the same reason.
 *
 * Visual direction: a split-screen layout, restrained brand palette, serif
 * heading, hairline borders, generous whitespace. Targets the polish that
 * high-net-worth clients expect from a fiduciary firm's portal.
 */

function useViewport() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return { width: w, isMobile: w <= 900 };
}

// Stat ticker that stretches the full page width across the top of the
// portal. CSS animation marquees through the list indefinitely; respects
// prefers-reduced-motion. Lives outside the split layout so the same
// scroll passes through both the form column and the brand column.
function AumTicker() {
  const items = [...PORTAL_HEADLINE_STATS, ...PORTAL_HEADLINE_STATS];
  return (
    <div style={{
      position: 'relative',
      overflow: 'hidden',
      background: t.navyDeep,
      borderBottom: `1px solid rgba(255,255,255,0.08)`,
      padding: '0.45rem 0',
    }}>
      {/* Faint goldenrod accent stripe at the very top edge of the page */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, transparent 0%, ${t.gold} 50%, transparent 100%)`,
      }} />
      <style>{`
        @keyframes portal-ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .portal-ticker-track {
          display: inline-flex;
          gap: 3rem;
          padding: 0 2rem;
          animation: portal-ticker-scroll 42s linear infinite;
          white-space: nowrap;
        }
        @media (prefers-reduced-motion: reduce) {
          .portal-ticker-track { animation: none; }
        }
      `}</style>
      <div className="portal-ticker-track">
        {items.map((s, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.5rem' }}>
            <span style={{
              fontFamily: t.fontSerif,
              fontWeight: 700,
              fontSize: '0.95rem',
              color: t.goldSoft,
              letterSpacing: '0.02em',
            }}>
              {s.value}
            </span>
            <span style={{
              fontFamily: t.fontSans,
              fontSize: '0.65rem',
              color: 'rgba(255,255,255,0.72)',
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
            }}>
              {s.label}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function PortalLogin() {
  const { isMobile } = useViewport();
  const navigate = useNavigate();
  // Sign-in is a small step machine:
  //   'credentials' → email + password
  //   'verify'      → established client enters the 6-digit code we emailed (2FA)
  //   'set-password'→ first login (or after a staff reset): choose a new password
  //   'forgot'      → "Forgot your password?": request an emailed reset link
  //   'reset'       → arrived from that link: choose a new password
  const [mode, setMode] = useState('credentials');
  // From the emailed link's #reset=<token>. A fragment rather than a query
  // string on purpose: fragments are never sent to a server, so the token
  // stays out of access logs and Referer headers.
  const [resetToken, setResetToken] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => { ensurePortalFonts(); }, []);

  useEffect(() => {
    const match = window.location.hash.match(/^#reset=(.+)$/);
    if (!match) return;
    setResetToken(match[1]);
    setMode('reset');
    // Drop the token from the address bar and history once read.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, []);

  // On success the backend returns a token; persist it and land on the dashboard.
  const completeSession = (data) => {
    // A real sign-in is never an impersonation — clear any stale marker left
    // in this browser from a prior staff "log in as" session.
    clearPortalImpersonator();
    setPortalToken(data.token);
    navigate('/portal/dashboard', { state: { client: data.client } });
  };

  // Step 1 — email + password. Branches to the code step (established client)
  // or the set-password step (first login / after a reset).
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    const cleaned = email.trim();
    if (!cleaned || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/portal/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleaned, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Intentionally generic — we don't reveal which part of the
        // credentials was wrong. Matches the backend's message.
        throw new Error(data?.detail || 'Invalid email or password.');
      }
      if (data.needs_password_change) {
        setMode('set-password');
        setNotice('Welcome! For your first sign-in, please choose your own password.');
      } else if (data.needs_verification) {
        setMode('verify');
        setNotice(`We emailed a 6-digit verification code to ${cleaned}. It expires in 15 minutes.`);
      } else if (data.token) {
        completeSession(data);
      } else {
        throw new Error('Sign-in failed. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Sign-in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2a — established client: verify the emailed 2FA code.
  const handleVerify = async (e) => {
    e.preventDefault();
    setError(null);
    const cleaned = email.trim();
    if (!code.trim()) {
      setError('Please enter the verification code from your email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/portal/verify_login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleaned, password, code: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Invalid or expired verification code.');
      completeSession(data);
    } catch (err) {
      setError(err.message || 'Could not verify the code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2b — first login: set a self-chosen password (no 2FA on first login).
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    const cleaned = email.trim();
    if (newPassword.length < 8) {
      setError('Your new password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The two passwords don’t match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/portal/set_password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleaned, password, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Could not set your password. Please try again.');
      completeSession(data);
    } catch (err) {
      setError(err.message || 'Could not set your password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Re-request a fresh 2FA code (re-runs step 1 silently).
  const handleResend = async () => {
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/portal/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Could not resend the code.');
      setNotice(`We emailed a new verification code to ${email.trim()}.`);
    } catch (err) {
      setError(err.message || 'Could not resend the code.');
    } finally {
      setSubmitting(false);
    }
  };

  // "Forgot your password?" - always answers the same way, whether or not the
  // address has an account (the backend makes the same promise).
  const handleForgot = async (e) => {
    e.preventDefault();
    setError(null);
    const cleaned = email.trim();
    if (!cleaned) {
      setError('Please enter your email address.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/portal/forgot_password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleaned }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Could not send the reset email. Please try again.');
      setMode('credentials');
      setPassword('');
      setNotice(`If an account exists for ${cleaned}, we have emailed a link to reset your password. It expires in 60 minutes.`);
    } catch (err) {
      setError(err.message || 'Could not send the reset email. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Arrived from the reset link: set the new password. This does not sign the
  // client in - they sign in with it next, and still get the emailed code.
  const handleReset = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('Your new password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The two passwords don’t match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/portal/reset_password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Could not reset your password. Please try again.');
      setResetToken(null);
      backToCredentials();
      setNotice('Your password has been updated. Please sign in with your new password.');
    } catch (err) {
      setError(err.message || 'Could not reset your password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const toForgot = () => {
    setMode('forgot');
    setError(null);
    setNotice(null);
  };

  const backToCredentials = () => {
    setMode('credentials');
    setError(null);
    setNotice(null);
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <div style={{
      // Lock to a single viewport so the whole sign-in chrome — ticker,
      // form, brand panel, bottom accent — fits without scrolling on
      // typical desktop sizes. `overflow: hidden` clips any rare overflow
      // (very short viewports) rather than introducing a scrollbar.
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: t.canvas,
      fontFamily: t.fontSans,
      color: t.ink,
    }}>

      {/* ── Top: AUM ticker (full page width) ───────────────────────────── */}
      <AumTicker />

      {/* ── Below: split (form / brand panel) ───────────────────────────── */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        minHeight: 0, // allow grid children to size to flex container, not content
      }}>

      {/* ── Left: sign-in form ──────────────────────────────────────────── */}
      <div style={{
        background: t.surface,
        padding: isMobile ? '1.5rem 1.5rem 2rem' : '2rem 3.5rem',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', // clip if a short viewport squeezes content
      }}>
        {/* Brand mark */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '2.5rem' }}>
          <div
            aria-hidden="true"
            style={{
              width: 30, height: 30, flexShrink: 0,
              background: t.gold,
              WebkitMask: 'url(/pca.png) center / contain no-repeat',
              mask: 'url(/pca.png) center / contain no-repeat',
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
            <span style={{ color: t.navy, fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.02em' }}>
              Prosperity Capital Advisors
            </span>
            <span style={{ color: t.gold, fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', marginTop:'4px' }}>
              Client Portal
            </span>
          </div>
        </div>

        {/* Heading + form, vertically centered on desktop, then lifted a
            touch via paddingBottom so the EMAIL ADDRESS label — rather than
            the "Welcome back" heading above it — lines up with the brand
            panel's centered "A higher standard of care" eyebrow on the right.
            (Bottom padding shrinks the centered content box from below, so the
            block shifts up by half the padding.) The logo stays pinned at the
            top, the footer at the bottom. On mobile the brand panel is hidden,
            so we keep the block top-aligned. */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          ...(isMobile ? {} : { flex: 1, justifyContent: 'center', minHeight: 0, paddingBottom: '9px' }),
        }}>
        {/* Heading — copy changes with the sign-in step */}
        <div style={{ marginBottom: '1.75rem' }}>
          <h1 style={{
            fontFamily: t.fontSerif,
            fontSize: isMobile ? '1.5rem' : '1.7rem',
            fontWeight: 700,
            color: t.navy,
            margin: 0,
            lineHeight: 1.15,
            letterSpacing: '-0.005em',
          }}>
            {mode === 'set-password' ? 'Set your password.'
              : mode === 'verify' ? 'Verify it’s you.'
                : mode === 'forgot' ? 'Reset your password.'
                  : mode === 'reset' ? 'Choose a new password.'
                    : 'Welcome back.'}
          </h1>
          <p style={{ color: t.inkSubtle, fontSize: '0.85rem', margin: '0.5rem 0 0', lineHeight: 1.55 }}>
            {mode === 'set-password' || mode === 'reset' ? 'Choose a password you’ll use to sign in from now on.'
              : mode === 'verify' ? 'Enter the one-time code we emailed you.'
                : mode === 'forgot' ? 'Enter your email address and we’ll send you a link to choose a new password.'
                  : 'View your secure documents and connect with your advisor.'}
          </p>
        </div>

        {/* Reusable error + info banners, shared across steps */}
        {error && (
          <div role="alert" style={{
            maxWidth: 400,
            background: 'rgba(154,28,28,0.06)',
            border: `1px solid rgba(154,28,28,0.30)`,
            color: t.error,
            borderRadius: 4,
            padding: '0.6rem 0.85rem',
            fontSize: '0.875rem',
            display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
            marginBottom: '0.9rem',
          }}>
            <i className="bi bi-exclamation-circle-fill" style={{ marginTop: 2 }} />
            <span>{error}</span>
          </div>
        )}
        {notice && !error && (
          <div style={{
            maxWidth: 400,
            background: 'rgba(0,105,166,0.06)',
            border: `1px solid rgba(0,105,166,0.28)`,
            color: t.navy,
            borderRadius: 4,
            padding: '0.6rem 0.85rem',
            fontSize: '0.85rem',
            display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
            marginBottom: '0.9rem',
          }}>
            <i className="bi bi-info-circle-fill" style={{ marginTop: 2, color: t.teal }} />
            <span>{notice}</span>
          </div>
        )}

        {mode === 'credentials' && (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', maxWidth: 400 }}>

          <div>
            <label htmlFor="portal-email" style={labelStyle(t)}>Email address</label>
            <input
              id="portal-email"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={submitting}
              style={inputStyle(t)}
              onFocus={(e) => focusInput(e, t)}
              onBlur={(e) => blurInput(e, t)}
            />
          </div>

          <div>
            <label htmlFor="portal-password" style={labelStyle(t)}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="portal-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                disabled={submitting}
                style={{ ...inputStyle(t), paddingRight: '2.75rem' }}
                onFocus={(e) => focusInput(e, t)}
                onBlur={(e) => blurInput(e, t)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'transparent', border: 'none', color: t.muted, cursor: 'pointer',
                  padding: '0.4rem 0.6rem', fontSize: '0.9rem',
                }}
              >
                <i className={`bi bi-${showPassword ? 'eye-slash' : 'eye'}`} />
              </button>
            </div>
          </div>

          <button type="submit" disabled={submitting} style={submitButtonStyle(t, submitting)}
            onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = t.navyDeep; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = t.navy; }}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.1rem' }}>
            <button type="button" onClick={toForgot} disabled={submitting} style={linkButtonStyle(t)}>
              Forgot your password?
            </button>
          </div>
        </form>
        )}

        {mode === 'forgot' && (
        <form onSubmit={handleForgot} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', maxWidth: 400 }}>
          <div>
            <label htmlFor="portal-forgot-email" style={labelStyle(t)}>Email address</label>
            <input
              id="portal-forgot-email"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={submitting}
              style={inputStyle(t)}
              onFocus={(e) => focusInput(e, t)}
              onBlur={(e) => blurInput(e, t)}
            />
          </div>

          <button type="submit" disabled={submitting} style={submitButtonStyle(t, submitting)}
            onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = t.navyDeep; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = t.navy; }}>
            {submitting ? 'Sending…' : 'Email me a reset link'}
          </button>

          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginTop: '0.1rem' }}>
            <button type="button" onClick={backToCredentials} style={linkButtonStyle(t)}>
              <i className="bi bi-chevron-left" /> Back to sign in
            </button>
          </div>
        </form>
        )}

        {mode === 'verify' && (
        <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', maxWidth: 400 }}>
          <div>
            <label htmlFor="portal-code" style={labelStyle(t)}>Verification code</label>
            <input
              id="portal-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              disabled={submitting}
              style={{ ...inputStyle(t), letterSpacing: '0.4em', fontWeight: 700 }}
              onFocus={(e) => focusInput(e, t)}
              onBlur={(e) => blurInput(e, t)}
            />
          </div>

          <button type="submit" disabled={submitting} style={submitButtonStyle(t, submitting)}
            onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = t.navyDeep; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = t.navy; }}>
            {submitting ? 'Verifying…' : 'Verify & sign in'}
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.1rem' }}>
            <button type="button" onClick={backToCredentials} style={linkButtonStyle(t)}>
              <i className="bi bi-chevron-left" /> Back
            </button>
            <button type="button" onClick={handleResend} disabled={submitting} style={linkButtonStyle(t)}>
              Resend code
            </button>
          </div>
        </form>
        )}

        {(mode === 'set-password' || mode === 'reset') && (
        <form onSubmit={mode === 'reset' ? handleReset : handleSetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', maxWidth: 400 }}>
          <div>
            <label htmlFor="portal-new-password" style={labelStyle(t)}>New password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="portal-new-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                autoFocus
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                disabled={submitting}
                style={{ ...inputStyle(t), paddingRight: '2.75rem' }}
                onFocus={(e) => focusInput(e, t)}
                onBlur={(e) => blurInput(e, t)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'transparent', border: 'none', color: t.muted, cursor: 'pointer',
                  padding: '0.4rem 0.6rem', fontSize: '0.9rem',
                }}
              >
                <i className={`bi bi-${showPassword ? 'eye-slash' : 'eye'}`} />
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="portal-confirm-password" style={labelStyle(t)}>Confirm new password</label>
            <input
              id="portal-confirm-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your new password"
              disabled={submitting}
              style={inputStyle(t)}
              onFocus={(e) => focusInput(e, t)}
              onBlur={(e) => blurInput(e, t)}
            />
          </div>

          <button type="submit" disabled={submitting} style={submitButtonStyle(t, submitting)}
            onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = t.navyDeep; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = t.navy; }}>
            {submitting ? 'Saving…' : mode === 'reset' ? 'Save new password' : 'Set password & sign in'}
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.1rem' }}>
            <button type="button" onClick={() => { setResetToken(null); backToCredentials(); }} style={linkButtonStyle(t)}>
              <i className="bi bi-chevron-left" /> {mode === 'reset' ? 'Back to sign in' : 'Back'}
            </button>
            {mode === 'reset' && error && (
              <button type="button" onClick={toForgot} disabled={submitting} style={linkButtonStyle(t)}>
                Request a new link
              </button>
            )}
          </div>
        </form>
        )}
        </div>

        {/* Footer — push to the bottom of the column */}
        <div style={{ marginTop: 'auto', paddingTop: '2.25rem', color: t.muted, fontSize: '0.65rem', lineHeight: 1.55 }}>
          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
            <span>© {new Date().getFullYear()} Prosperity Capital Advisors</span>
            <span>Registered Investment Adviser</span>
          </div>
          <div style={{ marginTop: '0.4rem', maxWidth: 520, display:'none' }}>
            Investments involve risk and are not guaranteed. Past performance does not guarantee future results.
            This portal is for the use of current PCA clients only.
          </div>
        </div>
      </div>

      {/* ── Right: brand panel ─────────────────────────────────────────── */}
      {/* Uses the same hero image the Hub login does (/homepage-hero-overlay.jpg)
          with a deep-navy gradient overlay so the type remains readable.
          Keeps a single visual language across both sign-in surfaces. */}
      {!isMobile && (
        <div style={{
          position: 'relative',
          overflow: 'hidden',
          backgroundImage: `url(/homepage-hero-overlay.jpg)`,
          backgroundSize: 'cover',
          // Anchor toward the RIGHT of the source image so the couple
          // (who sit on the right side of the original photo) stay in
          // frame instead of being cropped out by the panel's narrower
          // aspect ratio. `right center` would pin them to the edge;
          // `75% center` lets them sit slightly inside.
          backgroundPosition: '75% center',
          backgroundColor: t.navy,
          color: 'white',
          padding: '2rem 2.5rem',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Gradient overlay — navy heavy on edges, slightly lighter in
              middle so the underlying image breathes through. Mirrors the
              hub login's overlay treatment. */}
          <div style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(135deg, rgba(10,41,71,0.92) 0%, rgba(10,41,71,0.78) 45%, rgba(6,25,44,0.92) 100%)`,
          }} />

          {/* Main brand statement */}
          <div style={{
            position: 'relative', zIndex: 1,
            flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
            maxWidth: 520,
          }}>
            <div style={{
              color: t.goldSoft,
              fontFamily: t.fontSans,
              fontSize: '0.65rem',
              textTransform: 'uppercase',
              letterSpacing: '0.24em',
              fontWeight: 600,
              marginBottom: '1.1rem',
            }}>
              A higher standard of care
            </div>
            <h2 style={{
              fontFamily: t.fontSerif,
              fontSize: '1.7rem',
              lineHeight: 1.25,
              fontWeight: 500,
              margin: 0,
              letterSpacing: '-0.005em',
            }}>
              Stewardship, strategy, and a steady hand —{' '}
              <span style={{ color: t.goldSoft, fontStyle: 'italic' }}>for the long view.</span>
            </h2>
            <p style={{
              color: 'rgba(255,255,255,0.82)',
              fontSize: '0.85rem',
              lineHeight: 1.6,
              marginTop: '1.1rem',
              maxWidth: 460,
            }}>
              Your portal is the secure home for shared documents, signed agreements,
              and the conversations that shape your wealth strategy. Built and held to a
              fiduciary standard.
            </p>
          </div>

          {/* Trust strip at the bottom */}
          <div style={{
            position: 'relative', zIndex: 1,
            display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap',
            paddingTop: '1.1rem',
            borderTop: `1px solid rgba(255,255,255,0.14)`,
            color: 'rgba(255,255,255,0.72)',
            fontSize: '0.68rem',
            letterSpacing: '0.08em',
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <i className="bi bi-shield-lock" style={{ color: t.goldSoft }} />
              Bank-grade encryption
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <i className="bi bi-person-check" style={{ color: t.goldSoft }} />
              Fiduciary advice
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <i className="bi bi-graph-up-arrow" style={{ color: t.goldSoft }} />
              Independent &amp; transparent
            </span>
          </div>
        </div>
      )}
      </div>

      {/* Bottom goldenrod accent — mirrors the stripe at the top of the
          ticker so the page is symmetrically framed in brand color. */}
      <div style={{
        height: 2, flexShrink: 0,
        background: `linear-gradient(90deg, transparent 0%, ${t.gold} 50%, transparent 100%)`,
      }} />
    </div>
  );
}

// ─── Small styling helpers (kept inline to avoid an additional file) ────
function labelStyle(t) {
  return {
    display: 'block',
    color: t.inkSubtle,
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '0.35rem',
  };
}
function inputStyle(t) {
  return {
    width: '100%',
    background: t.surface,
    border: `1px solid ${t.hairlineStrong}`,
    borderRadius: 4,
    padding: '0.65rem 0.85rem',
    fontSize: '0.82rem',
    color: t.ink,
    outline: 'none',
    transition: 'border-color 0.18s, box-shadow 0.18s',
    fontFamily: t.fontSans,
  };
}
function focusInput(e, t) {
  e.currentTarget.style.borderColor = t.navy;
  e.currentTarget.style.boxShadow = `0 0 0 3px rgba(10,41,71,0.10)`;
}
function blurInput(e, t) {
  e.currentTarget.style.borderColor = t.hairlineStrong;
  e.currentTarget.style.boxShadow = 'none';
}
function submitButtonStyle(t, submitting) {
  return {
    marginTop: '0.3rem',
    background: t.navy,
    color: 'white',
    border: 0,
    borderRadius: 4,
    padding: '0.75rem 1.25rem',
    fontWeight: 600,
    fontSize: '0.82rem',
    letterSpacing: '0.04em',
    cursor: submitting ? 'wait' : 'pointer',
    transition: 'background 0.18s',
    fontFamily: t.fontSans,
    textTransform: 'uppercase',
  };
}
function linkButtonStyle(t) {
  return {
    background: 'transparent',
    border: 'none',
    color: t.teal,
    fontSize: '0.78rem',
    fontWeight: 500,
    cursor: 'pointer',
    padding: 0,
    fontFamily: t.fontSans,
  };
}
